import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiRequest } from '../utils/api'
import { List, Map, MapPin, Users, Trophy, Loader2, AlertCircle, Crown, X, Download } from 'lucide-react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import PageHeader from '../components/admin/PageHeader'
import DataTable from '../components/admin/DataTable'
import StatCard from '../components/admin/StatCard'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

const createCustomIcon = (color = '#3b82f6') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  })
}

const premiumIcon = createCustomIcon('#10b981')
const trialIcon = createCustomIcon('#f59e0b')
const freeIcon = createCustomIcon('#6b7280')

export default function Teams() {
  const [activeTab, setActiveTab] = useState('list')
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sportFilter, setSportFilter] = useState('')
  const [mapTeams, setMapTeams] = useState([])
  const [mapLoading, setMapLoading] = useState(false)
  const [mapError, setMapError] = useState(null)
  const [mapStats, setMapStats] = useState({ total: 0, withLocation: 0, premium: 0 })
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [planUpdating, setPlanUpdating] = useState(false)
  const [exporting, setExporting] = useState(false)

  useEffect(() => { loadTeams() }, [page, sportFilter])
  useEffect(() => {
    if (activeTab === 'map' && mapTeams.length === 0) fetchMapTeams()
  }, [activeTab])

  const loadTeams = async () => {
    try {
      setLoading(true)
      let url = `/api/admin/teams?page=${page}&limit=20`
      if (search) url += `&search=${encodeURIComponent(search)}`
      if (sportFilter) url += `&sport=${sportFilter}`
      const data = await apiRequest(url)
      setTeams(data)
    } catch (err) {
      console.error('Error loading teams:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMapTeams = async () => {
    try {
      setMapLoading(true)
      const data = await apiRequest('/api/admin/teams/map')
      setMapTeams(data.teams || [])
      setMapStats({ total: data.total || 0, withLocation: data.with_location || 0, premium: data.premium_count || 0 })
    } catch (err) { setMapError(err.message) }
    finally { setMapLoading(false) }
  }

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadTeams() }

  const handlePlanChange = async (newPlan, reason) => {
    if (!selectedTeam) return
    setPlanUpdating(true)
    try {
      await apiRequest(`/api/admin/teams/${selectedTeam.team_id}/plan`, {
        method: 'PUT', body: JSON.stringify({ plan: newPlan, reason })
      })
      setTeams(teams.map(t => t.team_id === selectedTeam.team_id ? { ...t, plan: newPlan } : t))
      setShowPlanModal(false)
      setSelectedTeam(null)
    } catch (err) { alert('Error updating plan: ' + err.message) }
    finally { setPlanUpdating(false) }
  }

  const getMarkerIcon = (team) => {
    if (team.plan === 'premium') return premiumIcon
    if (team.plan === 'trial') return trialIcon
    return freeIcon
  }

  const columns = [
    {
      key: 'name', header: 'Team', accessor: (row) => row.name,
      render: (row) => (
        <Link to={`/teams/${row.team_id}`} className="flex items-center gap-3 hover:text-indigo-600">
          {row.logo ? (
            <img src={row.logo} alt={`${row.name} logo`} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center flex-shrink-0">
              <span className="text-gray-600 font-semibold text-sm">{row.name?.charAt(0)?.toUpperCase() || 'T'}</span>
            </div>
          )}
          <span className="font-medium text-gray-900">{row.name}</span>
        </Link>
      ),
    },
    { key: 'sport', header: 'Sport', accessor: 'sport', render: (row) => <span className="text-gray-600 capitalize">{row.sport}</span> },
    {
      key: 'plan', header: 'Plan', accessor: (row) => row.plan || 'free',
      render: (row) => {
        const plan = row.plan || 'free'
        const cls = plan === 'premium' || plan === 'premium_monthly' || plan === 'premium_annual'
          ? 'bg-indigo-100 text-indigo-700' : plan === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
        return (
          <button onClick={() => { setSelectedTeam(row); setShowPlanModal(true) }}>
            <Badge className={`${cls} cursor-pointer hover:ring-2 hover:ring-offset-1`}>
              {plan === 'premium_monthly' ? 'premium monthly' : plan === 'premium_annual' ? 'premium annual' : plan}
            </Badge>
          </button>
        )
      },
    },
    {
      key: 'health', header: 'Health', accessor: 'health',
      render: (row) => <HealthBadge health={row.health} />,
    },
    { key: 'member_count', header: 'Members', accessor: 'member_count', render: (row) => <span className="text-gray-600">{row.member_count}</span> },
    { key: 'event_count', header: 'Events', accessor: 'event_count', render: (row) => <span className="text-gray-600">{row.event_count}</span> },
    { key: 'owner_name', header: 'Owner', accessor: 'owner_name', render: (row) => <span className="text-gray-600">{row.owner_name || '-'}</span> },
    {
      key: 'created_at', header: 'Created', accessor: 'created_at',
      render: (row) => <span className="text-gray-500">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</span>,
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Teams" description="View and manage all teams">
        <Button variant="outline" onClick={async () => {
          try {
            setExporting(true)
            const token = localStorage.getItem('admin_token')
            let url = `/api/admin/export/teams?`
            if (sportFilter) url += `sport=${sportFilter}&`
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}`, 'X-Environment': 'production' } })
            if (!res.ok) throw new Error('Export failed')
            const blob = await res.blob()
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = `squadline_teams_${new Date().toISOString().slice(0, 10)}.csv`
            document.body.appendChild(a); a.click(); a.remove()
            window.URL.revokeObjectURL(downloadUrl)
          } catch (err) { console.error('Export error:', err); alert('Failed to export teams') }
          finally { setExporting(false) }
        }} disabled={exporting}>
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
        <div className="flex bg-white border border-gray-200 p-1 rounded-xl">
          <Button variant={activeTab === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab('list')}>
            <List className="w-4 h-4 mr-1" /> List
          </Button>
          <Button variant={activeTab === 'map' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab('map')}>
            <Map className="w-4 h-4 mr-1" /> Map
          </Button>
        </div>
      </PageHeader>

      {activeTab === 'list' ? (
        <>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={sportFilter || 'all'} onValueChange={(val) => { setSportFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                <SelectItem value="soccer">Soccer</SelectItem>
                <SelectItem value="basketball">Basketball</SelectItem>
                <SelectItem value="baseball">Baseball</SelectItem>
                <SelectItem value="football">Football</SelectItem>
                <SelectItem value="hockey">Hockey</SelectItem>
                <SelectItem value="volleyball">Volleyball</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={teams}
              searchable
              searchPlaceholder="Search teams..."
              sortable
              emptyMessage="No teams found"
            />
          )}

          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <span className="text-sm text-gray-500">Page {page}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={teams.length < 20}>Next</Button>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {mapLoading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <span className="ml-2 text-gray-600">Loading team locations...</span>
            </div>
          ) : mapError ? (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-rose-500" />
              <span className="text-rose-700">{mapError}</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard title="Total Teams" value={mapStats.total} icon={Trophy} color="blue" />
                <StatCard title="With Location" value={mapStats.withLocation} icon={MapPin} color="emerald" />
                <StatCard title="Premium Teams" value={mapStats.premium} icon={Users} color="indigo" />
                <StatCard title="No Location" value={mapStats.total - mapStats.withLocation} icon={MapPin} color="gray" />
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-6">
                <span className="text-sm font-medium text-gray-800">Legend:</span>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-emerald-500"></div><span className="text-sm text-gray-600">Premium</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-amber-500"></div><span className="text-sm text-gray-600">Trial</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-gray-500"></div><span className="text-sm text-gray-600">Free</span></div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: '500px' }}>
                <MapContainer center={[39.8283, -98.5795]} zoom={4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {mapTeams.filter(t => t.coordinates).map((team) => (
                    <Marker key={team.team_id} position={[team.coordinates.lat, team.coordinates.lng]} icon={getMarkerIcon(team)}>
                      <Popup>
                        <div className="p-2 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            {team.logo ? <img src={team.logo} alt="" className="w-8 h-8 rounded-lg object-cover" /> : (
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"><Trophy className="w-4 h-4 text-gray-500" /></div>
                            )}
                            <h3 className="font-bold text-gray-900">{team.name}</h3>
                          </div>
                          <p className="text-gray-600 text-sm">{team.sport}</p>
                          <p className="text-gray-500 text-sm">{team.home_location}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <Badge className={team.plan === 'premium' ? 'bg-emerald-100 text-emerald-700' : team.plan === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}>{team.plan}</Badge>
                            <span className="text-xs text-gray-500">{team.member_count} members</span>
                          </div>
                          <Link to={`/teams/${team.team_id}`} className="mt-3 block text-center bg-indigo-600 text-white text-sm py-1 px-3 rounded hover:bg-indigo-700">View Team</Link>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>

              {mapTeams.filter(t => !t.coordinates).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Teams Without Location Data</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {mapTeams.filter(t => !t.coordinates).slice(0, 20).map(team => (
                      <Link key={team.team_id} to={`/teams/${team.team_id}`} className="text-sm text-indigo-600 hover:underline flex items-center gap-2">
                        {team.logo && <img src={team.logo} alt="" className="w-5 h-5 rounded object-cover" />}
                        {team.name}
                      </Link>
                    ))}
                    {mapTeams.filter(t => !t.coordinates).length > 20 && (
                      <span className="text-sm text-gray-500">+{mapTeams.filter(t => !t.coordinates).length - 20} more</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Plan Change Dialog */}
      <Dialog open={showPlanModal && !!selectedTeam} onOpenChange={(open) => { if (!open) { setShowPlanModal(false); setSelectedTeam(null) } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg"><Crown className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p>Change Team Plan</p>
                <p className="text-sm font-normal text-gray-500">{selectedTeam?.name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Current plan: <span className="font-medium">{selectedTeam?.plan || 'free'}</span></p>
            <PlanOption plan="free" label="Free" description="Basic features only" current={selectedTeam?.plan} onClick={() => handlePlanChange('free', 'Admin downgrade')} disabled={planUpdating} />
            <PlanOption plan="trial" label="Trial" description="Premium features for trial period" current={selectedTeam?.plan} onClick={() => handlePlanChange('trial', 'Admin trial grant')} disabled={planUpdating} color="amber" />
            <PlanOption plan="premium" label="Premium" description="Full premium access (admin granted)" current={selectedTeam?.plan} onClick={() => handlePlanChange('premium', 'Admin premium grant')} disabled={planUpdating} color="indigo" />
            <PlanOption plan="premium_monthly" label="Premium Monthly" description="Monthly subscription plan" current={selectedTeam?.plan} onClick={() => handlePlanChange('premium_monthly', 'Admin subscription grant')} disabled={planUpdating} color="indigo" />
            {planUpdating && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Updating plan...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function HealthBadge({ health }) {
  const config = {
    active: { cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: 'Active' },
    at_risk: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'At Risk' },
    inactive: { cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', label: 'Inactive' },
  }
  const c = config[health] || config.inactive
  return (
    <Badge className={c.cls}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} mr-1.5`} />
      {c.label}
    </Badge>
  )
}

function PlanOption({ plan, label, description, current, onClick, disabled, color = 'gray' }) {
  const isCurrent = current === plan
  const colors = {
    gray: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
    amber: 'border-amber-200 hover:border-amber-300 hover:bg-amber-50',
    indigo: 'border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50'
  }
  const activeColors = {
    gray: 'border-gray-500 bg-gray-50',
    amber: 'border-amber-500 bg-amber-50',
    indigo: 'border-indigo-500 bg-indigo-50'
  }
  return (
    <button
      onClick={onClick}
      disabled={disabled || isCurrent}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        isCurrent ? activeColors[color] : disabled ? 'opacity-50 cursor-not-allowed border-gray-200' : colors[color]
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        {isCurrent && <Badge className="bg-emerald-100 text-emerald-700">Current</Badge>}
      </div>
    </button>
  )
}
